import type { UserTelephonyAvailability } from '../types';

const READINESS_BLOCKED_EFFECTIVE_STATUSES = new Set<UserTelephonyAvailability['effectiveStatus']>([
  'operator_idle',
  'operator_needs_confirmation',
  'operator_hidden',
]);

/**
 * Return whether tenant policy enables blocking readiness recovery controls.
 *
 * Missing values preserve the behavior of payloads produced before the
 * readiness guard flag became part of the user availability contract.
 */
export function isOperatorReadinessGuardEnabled(
  availability: UserTelephonyAvailability | null | undefined,
): boolean {
  return availability?.readinessGuardEnabled !== false;
}

/**
 * Return whether the operator is blocked specifically by the readiness guard.
 *
 * The backend-owned `effectiveStatus` already accounts for tenant settings such
 * as disabling the automatic readiness guard. Raw `readinessState` is only a
 * legacy fallback for payloads that do not yet include the effective status.
 */
export function isOperatorReadinessBlockedByAvailability(
  availability: UserTelephonyAvailability | null | undefined,
  fallbackReadinessState?: UserTelephonyAvailability['readinessState'] | null,
): boolean {
  if (!availability || availability.status !== 'available') {
    return false;
  }
  if (!isOperatorReadinessGuardEnabled(availability)) {
    return false;
  }

  if (availability.effectiveStatus) {
    return READINESS_BLOCKED_EFFECTIVE_STATUSES.has(availability.effectiveStatus);
  }

  const readinessState = availability.readinessState ?? fallbackReadinessState;
  return Boolean(
    readinessState
      && readinessState !== 'ready'
      && readinessState !== 'not_applicable',
  );
}
